// COP data layer — the single seam between the COP dashboard and its approval,
// audit, and report data. It removes the duplication where CopApp reimplemented
// staged approvals + audit inline (a parallel build of the AOP approval/audit
// flow). The decision semantics here mirror the AOP approval service
// (AopPendingActionRecord: ordered approvalStages, a `client` stage for
// customer-facing actions, auto-approve creating an already-approved record).
//
// Two interchangeable backends implement the same method surface:
//   • live   — calls /api/v1/cop/* (which the server backs with the shared AOP
//              services, scoped by accountId + platform='cop').
//   • mock   — an in-memory backend seeded from copData, so the standalone
//              cop-preview (no server, no auth) keeps working.
//
// CopApp picks `live` when an auth token is present, else `mock`.
import AuthHelperMethods from '../auth/AuthHelperMethods';
import { makeAuditEvent } from './copData';

const Auth = new AuthHelperMethods();
const BASE = '/api/v1/cop';

const qs = (params = {}) => {
	const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== 'all');
	return entries.length ? '?' + new URLSearchParams(entries).toString() : '';
};

// ── live backend (server-backed, shared with AOP) ──────────────────────────
export const copLiveApi = {
	listApprovals: (params) => Auth.fetch(`${BASE}/recommendations${qs(params)}`, { method: 'GET' }),
	decideApproval: (id, decision, note) =>
		Auth.fetch(`${BASE}/recommendations/${id}/${decision === 'approved' ? 'approve' : decision === 'rejected' ? 'reject' : 'request-changes'}`, {
			method: 'POST',
			body: JSON.stringify({ note }),
		}),
	editApproval: (id, fields) => Auth.fetch(`${BASE}/recommendations/${id}/edit`, { method: 'POST', body: JSON.stringify(fields) }),
	submitForApproval: (approval) => Auth.fetch(`${BASE}/recommendations`, { method: 'POST', body: JSON.stringify(approval) }),
	listAudit: (params) => Auth.fetch(`${BASE}/audit${qs(params)}`, { method: 'GET' }),
	recordAudit: (event) => Auth.fetch(`${BASE}/audit`, { method: 'POST', body: JSON.stringify(event) }),
};

// ── shared decision semantics (one place; mirrors AOP approvalService) ──────
// Staged approve: an approve at a non-final stage advances the stage and keeps
// the record `submitted`; an approve at the final stage — or any reject /
// needs_changes — is the terminal decision.
export function applyApprovalDecision(approval, decision) {
	const stages = approval.approval_stages || ['internal'];
	const current = approval.stage || stages[0];
	const nextStage = stages[stages.indexOf(current) + 1];
	if (decision === 'approved' && nextStage) {
		return { approval: { ...approval, stage: nextStage }, advanced: true, nextStage };
	}
	return { approval: { ...approval, state: decision, stage: null }, advanced: false };
}

// ── live backend (server-backed; shared AOP store) ─────────────────────────
// Adapts copLiveApi responses to the same backend surface the mock exposes, so
// CopApp is agnostic. The server owns audit, so recordAudit just refreshes.
function unwrap(res, key) {
	if (!res || res.success === false) throw new Error((res && res.error) || 'COP request failed');
	return (res.data && res.data[key]) || [];
}

export function makeCopLiveBackend() {
	const listApprovals = async () => unwrap(await copLiveApi.listApprovals({}), 'recommendations');
	const listAudit = async () => unwrap(await copLiveApi.listAudit({}), 'events');
	const find = (list, id) => list.find((a) => a.approval_request_id === id) || null;

	return {
		mode: 'live',
		listApprovals,
		listAudit,
		// Audit is recorded server-side as part of each decision; refresh only.
		recordAudit: async () => listAudit(),
		decideApproval: async (id, decision) => {
			const target = find(await listApprovals(), id);
			await copLiveApi.decideApproval(id, decision);
			const approvals = await listApprovals();
			const updated = find(approvals, id);
			const advanced = Boolean(updated && updated.state === 'submitted' && updated.stage === 'client');
			return { approvals, target: target || updated, advanced };
		},
		editApproval: async (id, justification) => {
			const target = find(await listApprovals(), id);
			await copLiveApi.editApproval(id, { justification });
			const approvals = await listApprovals();
			return { approvals, target, updated: find(approvals, id) };
		},
		submitForApproval: async (approval) => {
			await copLiveApi.submitForApproval(approval);
			const approvals = await listApprovals();
			return { approvals, record: approvals[0] };
		},
	};
}

// Backend selector: live when the user is authenticated (real app), else the
// in-memory mock (standalone cop-preview, no server/auth).
export function hasCopAuth() {
	try {
		return new AuthHelperMethods().loggedIn();
	} catch {
		return false;
	}
}

export function makeCopBackend({ seed, live } = {}) {
	const useLive = live === undefined ? hasCopAuth() : live;
	return useLive ? makeCopLiveBackend() : makeCopMockBackend(seed);
}

// ── mock backend (in-memory; serverless preview) ───────────────────────────
// Holds its own approval/audit arrays and returns fresh snapshots so React
// state stays in sync. Pure of any server dependency.
export function makeCopMockBackend({ approvals = [], auditEvents = [] } = {}) {
	let _approvals = [...approvals];
	let _audit = [...auditEvents];

	const recordAudit = (partial) => {
		_audit = [makeAuditEvent(partial), ..._audit];
		return _audit;
	};

	return {
		mode: 'mock',
		listApprovals: () => _approvals,
		listAudit: () => _audit,
		recordAudit,

		decideApproval: (id, decision) => {
			const target = _approvals.find((a) => a.approval_request_id === id);
			if (!target || target.state !== 'submitted') return { approvals: _approvals, target: null };
			const { approval, advanced, nextStage } = applyApprovalDecision(target, decision);
			_approvals = _approvals.map((a) => (a.approval_request_id === id ? approval : a));
			return { approvals: _approvals, target, advanced, nextStage };
		},

		editApproval: (id, justification) => {
			const target = _approvals.find((a) => a.approval_request_id === id);
			if (!target || target.state !== 'submitted' || target.stage !== 'internal') return { approvals: _approvals, target: null };
			if (!justification || justification === target.justification) return { approvals: _approvals, target: null };
			const updated = { ...target, justification, edited: true };
			_approvals = _approvals.map((a) => (a.approval_request_id === id ? updated : a));
			return { approvals: _approvals, target, updated };
		},

		submitForApproval: (approval) => {
			const record = {
				approval_stages: ['internal', 'client'],
				stage: 'internal',
				...approval,
				state: 'submitted',
			};
			_approvals = [record, ..._approvals];
			return { approvals: _approvals, record };
		},
	};
}
